import { Metadata, parsePhoneNumberFromString } from "libphonenumber-js/core"
import metadataJson from "libphonenumber-js/min/metadata"

/** libphonenumber-js 的类型声明没覆盖号码格式模板相关的 API，这里按运行时实际结构补上 */
interface NumberFormat {
  pattern(): string
  leadingDigitsPatterns(): string[]
  internationalFormat(): string
}

interface MetadataWithFormats {
  selectNumberingPlan(countryCallingCode: string): void
  numberingPlan?: { formats(): NumberFormat[] }
}

const metadata = new Metadata(metadataJson) as unknown as MetadataWithFormats

/** 选格式模板：前导数字须匹配；完整号码还要求整串匹配模板，未完整（无效）号码只看前导数字 */
function chooseFormat(nationalNumber: string, isComplete: boolean) {
  return metadata.numberingPlan?.formats().find(item => {
    const leadingDigitsPatterns = item.leadingDigitsPatterns()
    const lastLeadingDigitsPattern = leadingDigitsPatterns[leadingDigitsPatterns.length - 1]

    if (lastLeadingDigitsPattern && nationalNumber.search(lastLeadingDigitsPattern) !== 0) {
      return false
    }

    return !isComplete || new RegExp(`^(?:${item.pattern()})$`).test(nationalNumber)
  })
}

/**
 * 按该国的国际格式模板排版并保留原有分隔符，如美国 +1 213-373-4253、中国 +86 187 7958 0591
 *
 * libphonenumber-js 的 formatInternational() 会把分隔符统一成空格（applyInternationalSeparatorStyle），
 * 这里按它选模板的逻辑（chooseFormatForNumber）自己套一遍 internationalFormat，跳过这一步
 *
 * 无效号码（位数不对等）按 libphonenumber 的 AsYouType 思路：把模板展开成 999-999-9999 这样的占位串，
 * 依次填入数字并截掉多余部分，如 +1222222223 → +1 222-222-223；套不上模板时原样返回
 */
function formatInternationalWithOriginalSeparators(countryCallingCode: string, nationalNumber: string, isValid: boolean) {
  const fallback = `+${countryCallingCode}${nationalNumber}`

  try {
    metadata.selectNumberingPlan(countryCallingCode)
  } catch {
    return fallback
  }

  const format = chooseFormat(nationalNumber, isValid)

  if (!format) {
    // 有效号码套不上模板时，与 libphonenumber-js 一致输出「+区号 号码」
    return isValid ? `+${countryCallingCode} ${nationalNumber}` : fallback
  }

  if (isValid) {
    return `+${countryCallingCode} ${nationalNumber.replace(new RegExp(format.pattern()), format.internationalFormat())}`
  }

  // 用足够长的占位数字匹配模板，得到该模板能容纳的最长号码
  const longestNumber = "999999999999999".match(new RegExp(format.pattern()))?.[0]

  if (!longestNumber || longestNumber.length < nationalNumber.length) {
    return fallback
  }

  const template = longestNumber.replace(new RegExp(format.pattern()), format.internationalFormat())
  let digitIndex = 0
  let formatted = ""

  for (const char of template) {
    if (digitIndex >= nationalNumber.length) {
      break
    }

    if (char === "9") {
      formatted += nationalNumber[digitIndex++]
    } else {
      formatted += char
    }
  }

  return `+${countryCallingCode} ${formatted}`
}

/**
 * 按 Shopify 订单详情的规则格式化手机号（Shopify 同样基于 libphonenumber）：
 * - 展示（默认）：按该国国际格式分组并保留其分隔符，如 +1 213-373-4253、+86 187 7958 0591；
 *   无效号码也尽量分组，如 +1222222223 → +1 222-222-223
 * - 复制（forCopy）：有效号码与展示相同，如 +1 213-373-4253；无效号码原样输出，如 +1222222223
 *
 * Shopify 返回 E.164（带 +）；后端历史字段是去掉 + 的 E.164，补上 + 后同样处理
 */
export function formatPhoneNumber(phone?: string | null, { forCopy = false } = {}): string {
  const value = (phone || "").trim()

  if (!value) {
    return ""
  }

  const e164 = /^\d+$/.test(value) ? `+${value}` : value
  const parsed = parsePhoneNumberFromString(e164, metadataJson)
  const isValid = Boolean(parsed?.isValid())

  if (forCopy) {
    return parsed && isValid ? formatInternationalWithOriginalSeparators(parsed.countryCallingCode, parsed.nationalNumber, true) : e164
  }

  return parsed ? formatInternationalWithOriginalSeparators(parsed.countryCallingCode, parsed.nationalNumber, isValid) : e164
}
